import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {DataSetDimensionsState} from './dataSetDimensions';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  loadPublicDataSetDimensions,
  loadPublicDataSetDimensionsSuccess,
  updateDataSetDimensionSuccess,
  updateDataSetDimensions,
  updateDataSetDimensionsSuccess,
} from './dataSetDimensions.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {DataSetDimension} from '../../interfaces/dimensions';
import {DimensionComponentObject, DimensionComponentObjects} from '../../classes/dimension-component-object';
import {Sort} from '../../libraries/sort';

const _reducer: ActionReducer<DataSetDimensionsState> = createReducer(
  {
    serialized: {
      all: [],
      normalized: [],
      source: []
    },
    componentObjects: {
      all: [],
      normalized: [],
      source: []
    },
    loading: false,
    loaded: false
  },
  on(loadDataSetDimensions, (state: DataSetDimensionsState): DataSetDimensionsState => {
    return {
      ...state,
      serialized: {
        all: [],
        normalized: [],
        source: []
      },
      componentObjects: {
        all: [],
        normalized: [],
        source: []
      },
      loading: true,
      loaded: false
    };
  }),
  on(loadDataSetDimensionsSuccess, (state: DataSetDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetDimension> }): DataSetDimensionsState => {
    const serializedNormalized: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'normalized');
    const serializedSource: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'source');
    const objectsNormalized: DimensionComponentObjects = serializedNormalized.map((dimension: Serializer<DataSetDimension>): DimensionComponentObject => new DimensionComponentObject(dimension));
    const objectsSource: DimensionComponentObjects = serializedSource.map((dimension: Serializer<DataSetDimension>): DimensionComponentObject => new DimensionComponentObject(dimension));

    return {
      ...state,
      serialized: {
        all: action.dimensions,
        normalized: serializedNormalized,
        source: serializedSource
      },
      componentObjects: {
        all: _sort([
          ...objectsNormalized,
          ...objectsSource
        ]),
        normalized: _sort(objectsNormalized),
        source: _sort(objectsSource)
      },
      loading: false,
      loaded: true
    }
  }),
  on(loadPublicDataSetDimensions, (state: DataSetDimensionsState): DataSetDimensionsState => {
    return {
      ...state,
      loading: true
    };
  }),
  on(loadPublicDataSetDimensionsSuccess, (state: DataSetDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetDimension> }): DataSetDimensionsState => {
    const serializedNormalized: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'normalized');
    const serializedSource: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'source');
    const objectsNormalized: DimensionComponentObjects = serializedNormalized.map((dimension: Serializer<DataSetDimension>): DimensionComponentObject => new DimensionComponentObject(dimension));
    const objectsSource: DimensionComponentObjects = serializedSource.map((dimension: Serializer<DataSetDimension>): DimensionComponentObject => new DimensionComponentObject(dimension));

    return {
      ...state,
      serialized: {
        all: action.dimensions,
        normalized: serializedNormalized,
        source: serializedSource
      },
      componentObjects: {
        all: [
          ...objectsNormalized,
          ...objectsSource
        ],
        normalized: objectsNormalized,
        source: objectsSource
      },
      loading: false,
      loaded: true
    }
  }),
  on(updateDataSetDimensions, (state: DataSetDimensionsState): DataSetDimensionsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(updateDataSetDimensionsSuccess, (state: DataSetDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetDimension> }): DataSetDimensionsState => {
    const serializedNormalized: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'normalized');
    const serializedSource: Serializers<DataSetDimension> = action.dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'source');

    action.dimensions.forEach((dimension: Serializer<DataSetDimension>): void => {
      const componentObject: DimensionComponentObject = state.componentObjects.all.find((componentObject: DimensionComponentObject): boolean => componentObject.payload.id === dimension.id);

      if (componentObject) {
        componentObject.update(dimension);
      } else {
        state.componentObjects.all.push(new DimensionComponentObject(dimension));
      }
    });

    state.componentObjects.all = state.componentObjects.all.filter((dimension: DimensionComponentObject): boolean => action.dimensions.includes(dimension.payload as Serializer<DataSetDimension>));

    return {
      ...state,
      serialized: {
        all: action.dimensions,
        normalized: serializedNormalized,
        source: serializedSource
      },
      componentObjects: {
        all: _sort(state.componentObjects.all),
        normalized: _sort(state.componentObjects.all.filter((dimension: DimensionComponentObject): boolean => (dimension.payload.attributes as DataSetDimension).type === 'normalized')),
        source: _sort(state.componentObjects.all.filter((dimension: DimensionComponentObject): boolean => (dimension.payload.attributes as DataSetDimension).type === 'source'))
      },
      loading: false,
      loaded: true
    }
  }),
  on(updateDataSetDimensionSuccess, (state: DataSetDimensionsState, action: TypedAction<string> & { dimension: Serializer<DataSetDimension> }): DataSetDimensionsState => {
    const dimensions: Serializers<DataSetDimension> = state.serialized.all.map((dimension: Serializer<DataSetDimension>): Serializer<DataSetDimension> => dimension.id === action.dimension.id && action.dimension || dimension);
    const serializedNormalized: Serializers<DataSetDimension> = dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'normalized');
    const serializedSource: Serializers<DataSetDimension> = dimensions.filter((dimension: Serializer<DataSetDimension>): boolean => dimension.attributes.type === 'source');

    state.componentObjects.all.find((dimension: DimensionComponentObject): boolean => dimension.payload.id === action.dimension.id).update(action.dimension);

    return {
      ...state,
      serialized: {
        all: dimensions,
        normalized: serializedNormalized,
        source: serializedSource
      },
      componentObjects: {
        all: _sort(state.componentObjects.all),
        normalized: _sort(state.componentObjects.normalized),
        source: _sort(state.componentObjects.source)
      },
    }
  })
);

export const dataSetDimensionsReducer: ActionReducer<DataSetDimensionsState> = (state: DataSetDimensionsState, action: Action) => {
  return _reducer(state, action);
}

function _sort(dimensions: DimensionComponentObjects): DimensionComponentObjects {
  return dimensions.sort((a: DimensionComponentObject, b: DimensionComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}
